import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const PLATFORMS = [
  ['google_ads', 'Google Ads'],
  ['yandex_metrika', 'Yandex Metrica'],
  ['meta', 'Meta'],
  ['vk', 'VK Ads'],
];

/*
  S2S postback log: "Download for an ad platform" — conversions of a period as a
  CSV for manual upload (GET /api/conversions/export-for-platform).
*/
export default function PlatformExport() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('google_ads');
  const [days, setDays] = useState('30');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(API + '/conversions/export-for-platform', {
        params: { platform, days: Number(days), name },
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'conversions-' + platform + '.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      const rows = res.headers && res.headers['x-rows'];
      toast.success(t('Downloaded conversions: {count}', { count: rows || 0 }));
    } catch (_e) {
      toast.error(t('Could not prepare the file'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="platform-export-open">
        <Download className="w-4 h-4 mr-2" />
        {t('Download for an ad platform')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Download for an ad platform')}</DialogTitle>
            <DialogDescription>
              {t('Conversions of the period as a file for manual upload. Only conversions whose click has the platform click id are included (gclid, yclid, rb_clickid; for Meta fbclid, fbc, e-mail or phone).')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('Ad platform')}</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="platform-export-platform"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t('Period')}</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['7', '30', '90'].map((d) => (<SelectItem key={d} value={d}>{t('Last {days} days', { days: d })}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="platform-export-name">{t('Conversion name or goal (optional)')}</Label>
              <Input id="platform-export-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Purchase" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={download} disabled={busy} data-testid="platform-export-download">{t('Download CSV')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
